import { useState, useRef, useEffect } from "react";
import { Camera, Upload, Edit, X, Check, Clock, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/Logo";

interface FichaStats {
  id: string;
  codigo_ficha: string | null;
  nome_cliente: string | null;
  tempo_processamento: number | null;
  created_at: string;
}

interface FichaErro {
  id: string;
  codigo_ficha: string | null;
  nome_cliente: string | null;
  created_at: string;
}

const TesteEnvio = () => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [ultimasFichas, setUltimasFichas] = useState<FichaStats[]>([]);
  const [errosRecentes, setErrosRecentes] = useState<FichaErro[]>([]);

  const carregarDados = async () => {
    const [fichasRes, errosRes] = await Promise.all([
      supabase
        .from("fichas")
        .select("id, codigo_ficha, nome_cliente, tempo_processamento, created_at")
        .not("tempo_processamento", "is", null)
        .order("created_at", { ascending: false })
        .limit(5),
      supabase
        .from("fichas")
        .select("id, codigo_ficha, nome_cliente, created_at")
        .eq("status", "erro")
        .order("created_at", { ascending: false })
        .limit(5),
    ]);
    if (fichasRes.data) setUltimasFichas(fichasRes.data);
    if (errosRes.data) setErrosRecentes(errosRes.data);
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const sendImageToWebhook = async (file: File) => {
    try {
      setIsUploading(true);
      const user = (await supabase.auth.getUser()).data.user;
      const formData = new FormData();
      formData.append('image', file);
      formData.append('user_id', user?.id || '');

      const { data, error } = await supabase.functions.invoke('processar-ficha-v2', {
        body: formData,
      });

      if (error) throw error;

      if (data.ficha_id) {
        toast.success("Ficha criada! Aguardando processamento...");
        setTimeout(carregarDados, 35000);
        navigate(`/editar-ficha/${data.ficha_id}`, {
          state: { imageFile: file, isNewFicha: true },
        });
      } else {
        throw new Error(data.error || 'Erro ao criar ficha no banco');
      }
    } catch (error: any) {
      console.error('Erro:', error);
      toast.error("Falha ao criar a ficha. Tente novamente.");
      setShowErrorDialog(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNovaFoto = () => {
    setShowErrorDialog(false);
    setSelectedFile(null);
    cameraInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setShowConfirmDialog(true);
      } else {
        toast.error("Por favor, selecione uma imagem válida.");
      }
    }
  };

  const handleConfirmSend = () => {
    if (selectedFile) {
      sendImageToWebhook(selectedFile);
      setShowConfirmDialog(false);
    }
  };

  const handleCancelSend = () => {
    setShowConfirmDialog(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
  };

  const formatarData = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
      ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const labelFicha = (f: { codigo_ficha: string | null; nome_cliente: string | null; id: string }) =>
    f.codigo_ficha ? `#${f.codigo_ficha}` : f.nome_cliente ?? f.id.slice(0, 8);

  const media = ultimasFichas.length
    ? Math.round(ultimasFichas.reduce((acc, f) => acc + (f.tempo_processamento ?? 0), 0) / ultimasFichas.length)
    : null;

  return (
    <div className="min-h-screen bg-background pb-20 relative">
      <Header title="Teste de Envio (v2)" />

      {/* Logo de fundo */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none opacity-5 z-0">
        <Logo className="w-96 h-96 object-contain" />
      </div>

      <main className="px-4 py-6 relative z-10">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-4 items-start">

          {/* ── Coluna esquerda: formulário de envio ── */}
          <div className="w-full md:w-96 shrink-0">
            <div className="bg-card rounded-lg p-8 shadow-sm">
              <div className="flex justify-center mb-4">
                <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded tracking-wide">
                  AMBIENTE DE TESTE — v2 (OpenAI direto)
                </span>
              </div>

              <div className="flex justify-center mb-6">
                <div className="w-32 h-32 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center">
                  <Camera className="w-16 h-16 text-amber-500" />
                </div>
              </div>

              <div className="text-center mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  [TESTE] Capturar Ficha
                </h2>
                <p className="text-muted-foreground text-sm">
                  OCR direto via OpenAI. Tire uma foto ou carregue uma imagem.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <Button
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 hover:shadow-lg transition-all text-white"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  {isUploading ? "Enviando..." : "[Teste] Tirar Foto"}
                </Button>

                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-full h-12 bg-gradient-to-r from-amber-100 to-amber-200 hover:from-amber-200 hover:to-amber-300 hover:shadow-lg transition-all text-amber-800 border border-amber-300"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  [Teste] Carregar Imagem
                </Button>
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-card text-muted-foreground">ou</span>
                </div>
              </div>

              <button className="w-full py-3 text-amber-600 hover:text-amber-800 transition-colors flex items-center justify-center gap-2">
                <Edit className="w-4 h-4" />
                <span className="font-medium">[Teste] Cadastrar Manualmente</span>
              </button>

              <input ref={cameraInputRef} id="camera-input" name="camera-input" type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
              <input ref={fileInputRef} id="file-input" name="file-input" type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
            </div>
          </div>

          {/* ── Coluna direita: stats ── */}
          <div className="w-full flex flex-col gap-4">

            {/* Últimas fichas processadas */}
            <div className="bg-card rounded-lg p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-foreground">Últimas fichas processadas</h3>
              </div>

              {ultimasFichas.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhuma ficha processada ainda.</p>
              ) : (
                <>
                  <div className="space-y-2 mb-4">
                    {ultimasFichas.map((f) => (
                      <div key={f.id} className="flex items-center justify-between text-sm">
                        <div className="flex flex-col min-w-0">
                          <span className="text-foreground truncate">{labelFicha(f)}</span>
                          <span className="text-xs text-muted-foreground">{formatarData(f.created_at)}</span>
                        </div>
                        <span className="font-mono font-medium text-amber-600 ml-4 shrink-0">
                          {f.tempo_processamento}s
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Média de processamento</span>
                    <span className="font-mono font-bold text-amber-600">{media}s</span>
                  </div>
                </>
              )}
            </div>

            {/* Erros recentes */}
            <div className="bg-card rounded-lg p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <h3 className="text-sm font-semibold text-foreground">Erros recentes</h3>
                {errosRecentes.length > 0 && (
                  <span className="ml-auto text-xs font-bold bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                    {errosRecentes.length}
                  </span>
                )}
              </div>

              {errosRecentes.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum erro recente.</p>
              ) : (
                <div className="space-y-2">
                  {errosRecentes.map((f) => (
                    <div key={f.id} className="flex items-center justify-between text-sm">
                      <div className="flex flex-col min-w-0">
                        <span className="text-foreground truncate">{labelFicha(f)}</span>
                        <span className="text-xs text-muted-foreground">{formatarData(f.created_at)}</span>
                      </div>
                      <button
                        onClick={() => navigate(`/editar-ficha/${f.id}`)}
                        className="ml-4 shrink-0 text-xs text-amber-600 hover:text-amber-800 underline"
                      >
                        ver
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </main>

      {/* Dialogs */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Imagem</DialogTitle>
            <DialogDescription>Verifique se a imagem está correta antes de enviar</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="relative w-full aspect-[4/3] bg-muted rounded-lg overflow-hidden">
              {previewUrl && <img src={previewUrl} alt="Prévia" className="w-full h-full object-contain" />}
            </div>
            <p className="text-sm text-muted-foreground text-center">Esta é a imagem que será enviada. Confirma?</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelSend} disabled={isUploading} className="flex-1">
              <X className="w-4 h-4 mr-2" />Cancelar
            </Button>
            <Button onClick={handleConfirmSend} disabled={isUploading} className="flex-1">
              <Check className="w-4 h-4 mr-2" />{isUploading ? "Enviando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Erro ao processar imagem</AlertDialogTitle>
            <AlertDialogDescription>Não foi possível extrair os dados da ficha. Tente novamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={handleNovaFoto} className="w-full sm:w-auto">
              <Camera className="w-4 h-4 mr-2" />Tirar Nova Foto
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
};

export default TesteEnvio;
